import { type ApiResponse, AppError, type ErrorCode } from '@momonos/shared'

/**
 * The typed HTTP client every app uses.
 *
 * Owns two things the apps must never each reinvent: unwrapping the response
 * envelope into a thrown AppError, and refreshing an expired access token
 * exactly once before retrying (FR-AUTH-4).
 */

export interface ClientOptions {
  baseUrl: string
  /** Returns the current access token, or null when signed out. */
  getAccessToken?: () => string | null
  /** Called after a refresh so the app can store the new token. */
  onTokenRefreshed?: (accessToken: string) => void
  /** Called when the session is gone for good and the user must sign in. */
  onSessionExpired?: () => void
  fetchImpl?: typeof fetch
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE'
  body?: unknown
  query?: Record<string, string | number | boolean | undefined | null>
  /** FR-ORD-6 — set on order submits so a retry cannot double-charge. */
  idempotencyKey?: string
  signal?: AbortSignal
  /** Internal: prevents a refresh loop. */
  isRetry?: boolean
}

export class ApiClient {
  private refreshInFlight: Promise<boolean> | null = null

  constructor(private readonly options: ClientOptions) {}

  get baseUrl(): string {
    return this.options.baseUrl
  }

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const doFetch = this.options.fetchImpl ?? fetch
    const url = new URL(path.replace(/^\//, ''), `${this.options.baseUrl.replace(/\/$/, '')}/`)

    for (const [key, value] of Object.entries(options.query ?? {})) {
      if (value != null) url.searchParams.set(key, String(value))
    }

    const headers: Record<string, string> = { Accept: 'application/json' }
    if (options.body !== undefined) headers['Content-Type'] = 'application/json'
    if (options.idempotencyKey) headers['idempotency-key'] = options.idempotencyKey

    const token = this.options.getAccessToken?.()
    if (token) headers.Authorization = `Bearer ${token}`

    const response = await doFetch(url.toString(), {
      method: options.method ?? 'GET',
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      // The refresh cookie is httpOnly and lives on the API origin.
      credentials: 'include',
      signal: options.signal,
    })

    let payload: ApiResponse<T>
    try {
      payload = (await response.json()) as ApiResponse<T>
    } catch {
      throw new AppError(
        'INTERNAL',
        `The server returned an unreadable response (${response.status})`,
      )
    }

    if (payload.ok) return payload.data

    // An expired access token is recoverable exactly once.
    if (payload.error.code === 'TOKEN_EXPIRED' && !options.isRetry) {
      if (await this.refresh()) {
        return this.request<T>(path, { ...options, isRetry: true })
      }
    }

    if (payload.error.code === 'UNAUTHORIZED' || payload.error.code === 'TOKEN_EXPIRED') {
      this.options.onSessionExpired?.()
    }

    throw new AppError(
      payload.error.code as ErrorCode,
      payload.error.message,
      payload.error.details,
    )
  }

  /** Concurrent 401s share one refresh, so a screen does not stampede. */
  private async refresh(): Promise<boolean> {
    this.refreshInFlight ??= this.performRefresh().finally(() => {
      this.refreshInFlight = null
    })
    return this.refreshInFlight
  }

  private async performRefresh(): Promise<boolean> {
    const doFetch = this.options.fetchImpl ?? fetch

    try {
      const response = await doFetch(`${this.options.baseUrl.replace(/\/$/, '')}/v1/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      })

      const payload = (await response.json()) as ApiResponse<{ accessToken: string }>
      if (!payload.ok) return false

      this.options.onTokenRefreshed?.(payload.data.accessToken)
      return true
    } catch {
      return false
    }
  }

  get<T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}) {
    return this.request<T>(path, { ...options, method: 'GET' })
  }

  post<T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method'> = {}) {
    return this.request<T>(path, { ...options, method: 'POST', body })
  }

  patch<T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method'> = {}) {
    return this.request<T>(path, { ...options, method: 'PATCH', body })
  }
}
